import { useEffect, useId, useRef, useState, type ReactNode } from "react";

type MenuItem = {
  id: string;
  label: string;
  onClick: () => void;
  disabled?: boolean;
};

type Props = {
  label: ReactNode;
  items: MenuItem[];
  disabled?: boolean;
  align?: "start" | "end";
};

export function Menu({ label, items, disabled, align = "start" }: Props) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className={`surf-menu surf-menu--${align}`} ref={rootRef}>
      <button
        type="button"
        className="surf-menu-trigger"
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        onClick={() => setOpen((v) => !v)}
      >
        {label}
        <span aria-hidden> ▾</span>
      </button>
      {open && (
        <ul id={menuId} className="surf-menu-dropdown" role="menu">
          {items.map((item) => (
            <li key={item.id} role="none">
              <button
                type="button"
                role="menuitem"
                className="surf-menu-item"
                disabled={item.disabled}
                onClick={() => {
                  setOpen(false);
                  item.onClick();
                }}
              >
                {item.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
